import { FALLBACK_AMOUNTS, TRIAL_DAYS, type Interval, type Tier } from "@/constants/tier";
import { fmtAmount } from "@/lib/format-price";
import { hasAnyPrice, parsePricing, type CatalogPricing } from "@/lib/pricing-types";

/**
 * Catalog amounts for the marketing pages, from the backend's public
 * GET /api/saas/pricing (the Paddle prices behind PADDLE_PRICE_*, cached a day
 * there). Cached here for a day as well (ISR, tag "pricing" — POST
 * /api/revalidate { "tag": "pricing" } after a price change), so a Paddle or
 * backend hiccup never reaches a visitor; `null` = use the static fallback.
 */
export interface PublicPricing {
  pricing: CatalogPricing;
  trialDays: number;
}

export const PRICING_REVALIDATE_SECONDS = 24 * 60 * 60;
export const PRICING_CACHE_TAG = "pricing";

export async function getPublicPricing(): Promise<PublicPricing | null> {
  const base = process.env.STRAPI_API_URL;
  if (!base) return null;
  try {
    const res = await fetch(`${base.replace(/\/$/, "")}/api/saas/pricing`, {
      headers: { accept: "application/json" },
      next: { revalidate: PRICING_REVALIDATE_SECONDS, tags: [PRICING_CACHE_TAG] },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { pricing?: unknown; trialDays?: unknown };
    const pricing = parsePricing(data.pricing);
    if (!hasAnyPrice(pricing)) return null;
    return {
      pricing,
      trialDays: typeof data.trialDays === "number" && data.trialDays > 0 ? data.trialDays : TRIAL_DAYS,
    };
  } catch (error) {
    console.error("[pricing] public pricing unavailable, using the static copy:", error);
    return null;
  }
}

export interface DisplayPrice {
  /** Whole euros shown as the headline (yearly = total / 12, rounded). */
  perMonth: number;
  /** What is billed per cycle (yearly total for yearly plans). */
  total: number;
  currency: string;
}

/** Live amounts where the catalog has them, the static fallback elsewhere. */
export function displayPrices(pricing: CatalogPricing | null | undefined): Record<Tier, Record<Interval, DisplayPrice>> {
  const out = {} as Record<Tier, Record<Interval, DisplayPrice>>;
  for (const tier of ["solo", "team"] as const) {
    out[tier] = {} as Record<Interval, DisplayPrice>;
    for (const interval of ["monthly", "yearly"] as const) {
      const live = pricing?.[tier]?.[interval];
      if (live && Number.isFinite(live.amount) && live.amount > 0) {
        const total = live.amount / 100;
        out[tier][interval] = {
          total,
          perMonth: interval === "yearly" ? Math.round(total / 12) : total,
          currency: live.currency,
        };
      } else {
        out[tier][interval] = { ...FALLBACK_AMOUNTS[tier][interval], currency: "EUR" };
      }
    }
  }
  return out;
}

/** "€19" / "19 €" and "€69" / "69 €" — the monthly headline amounts for prose and metadata. */
export function headlineAmounts(pricing: CatalogPricing | null | undefined, locale: string): { solo: string; team: string } {
  const p = displayPrices(pricing);
  return {
    solo: fmtAmount(p.solo.monthly.perMonth, p.solo.monthly.currency, locale),
    team: fmtAmount(p.team.monthly.perMonth, p.team.monthly.currency, locale),
  };
}

/** Replaces {solo} / {team} / {days} in marketing copy. */
export const fillPricingCopy = (s: string, vars: Record<string, string | number>) =>
  Object.entries(vars).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(String(v)), s);
